import React from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AccountingPricingSection = ({ onWaitlistClick }) => {
  const appUrl = (import.meta.env.VITE_APP_URL || 'https://app.neevii.com/').replace(/\/$/, '');

  return (
    <section className="py-20 bg-background">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-card/50 backdrop-blur-sm border border-border rounded-full px-4 py-2">
            <Icon name="Shield" size={16} className="text-green-400" />
            <span className="text-sm text-muted-foreground font-medium">
              Anti-SaaS Pricing
            </span>
          </div>

          <h2 className="text-4xl lg:text-5xl font-bold text-foreground">
            Own your accounting.
            <span className="gradient-text block">Not a subscription.</span>
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            No monthly fees. No Open Banking APIs. Your data stays on your machine.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Tier 1: Basic */}
          <div className="relative p-8 rounded-2xl border border-border bg-card/50 depth-layer">
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="inline-flex items-center space-x-2 bg-blue-400/10 rounded-full px-3 py-1">
                  <Icon name="Zap" size={14} className="text-blue-400" />
                  <span className="text-xs font-medium text-blue-400">Free Forever</span>
                </div>
                <h3 className="text-2xl font-bold text-foreground">Basic</h3>
                <p className="text-sm text-muted-foreground">
                  Everything you need to stay organised — no strings attached.
                </p>
              </div>

              <div className="flex items-baseline space-x-1">
                <span className="text-5xl font-bold text-foreground">$0</span>
                <span className="text-muted-foreground">/forever</span>
              </div>

              <div className="space-y-3">
                {[
                  'Manual CSV Imports',
                  'Absolute Privacy & Offline Mode',
                  'Manual Transaction Categorization',
                  'Basic Financial Dashboard',
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <Icon name="Check" size={16} className="text-green-400 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              <a href={appUrl}>
                <Button variant="outline" fullWidth className="border-border hover:bg-card/80">
                  Try Basic (Free)
                </Button>
              </a>
            </div>
          </div>

          {/* Tier 2: Pro */}
          <div className="relative p-8 rounded-2xl border border-primary/50 bg-primary/5 depth-layer scale-[1.02]">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                Pay Once. Own Forever.
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <div className="inline-flex items-center space-x-2 bg-green-400/10 rounded-full px-3 py-1">
                  <Icon name="BadgeCheck" size={14} className="text-green-400" />
                  <span className="text-xs font-medium text-green-400">One-time purchase</span>
                </div>
                <h3 className="text-2xl font-bold text-foreground">Pro License</h3>
                <p className="text-sm text-muted-foreground">
                  Everything in Basic, plus local AI that runs on your machine.
                </p>
              </div>

              <div className="flex items-baseline space-x-1">
                <span className="text-5xl font-bold text-foreground">$99</span>
                <span className="text-muted-foreground">pay once</span>
              </div>

              <div className="space-y-3">
                {[
                  { text: 'Everything in Basic', isBasic: true },
                  { text: 'Local AI Auto-Categorization (WASM)', isBasic: false },
                  { text: 'Local Receipt OCR', isBasic: false },
                  { text: 'One-Click Tax Report Export', isBasic: false },
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center space-x-3">
                    <Icon
                      name="Check"
                      size={16}
                      className={feature.isBasic ? 'text-muted-foreground/40 flex-shrink-0' : 'text-green-400 flex-shrink-0'}
                    />
                    <span className={`text-sm ${feature.isBasic ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>

              <Button
                variant="default"
                fullWidth
                className="bg-brand text-slate-900 shadow-xl shadow-brand/20 hover:shadow-brand/35 transition-all duration-300"
                onClick={onWaitlistClick}
                iconName="Mail"
                iconPosition="left"
              >
                Join Waitlist
              </Button>
            </div>
          </div>
        </div>

        {/* Trust footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">No Open Banking APIs.</span>{' '}
            Your CSV, your machine, your control. Built by a UK freelancer — not a VC-backed platform.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AccountingPricingSection;
